/**
 * LOOP UPLOAD PROGRESS BANNER
 * 
 * Shows actual upload progress with steps:
 * 1. Validating → 2. Compressing → 3. Uploading → 4. Saving → 5. Done
 * 
 * Features:
 * - Clean banner UI (not blocking modal)
 * - Shows current step
 * - Progress bar
 * - Timeout protection (auto-fails after 60s)
 * - Dismissable
 */

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, Upload, X } from 'lucide-react';
import { cn } from '@/lib/utils';

type UploadStep = 'validating' | 'compressing' | 'uploading' | 'saving' | 'done' | 'error';

interface UploadProgressProps {
  isUploading: boolean;
  onDismiss?: () => void;
}

const STEP_INFO = {
  validating: { label: 'Validating file...', progress: 10 },
  compressing: { label: 'Compressing image...', progress: 30 },
  uploading: { label: 'Uploading...', progress: 60 },
  saving: { label: 'Saving post...', progress: 90 },
  done: { label: 'Posted successfully!', progress: 100 },
  error: { label: 'Upload failed', progress: 0 }
};

const UPLOAD_TIMEOUT = 300000; // 5 minutes for slow connections

export default function LoopUploadProgress({ isUploading, onDismiss }: UploadProgressProps) {
  const [currentStep, setCurrentStep] = useState<UploadStep>('validating');
  const [progress, setProgress] = useState(0);
  const [timeoutError, setTimeoutError] = useState(false);

  // Listen to post-flow events from bulletin form
  useEffect(() => {
    if (!isUploading) {
      setCurrentStep('validating');
      setProgress(0);
      setTimeoutError(false);
      return;
    }

    console.log('[🔵 Upload Progress] Starting upload tracking');

    const handleFlowUpdate = (event: CustomEvent) => {
      const { key, state, detail } = event.detail;
      
      console.log('[🔵 Upload Progress] Event received:', { 
        key, 
        state, 
        detail,
        timestamp: new Date().toISOString()
      });
      
      // Map bulletin flow events to upload steps
      if (key === 'upload') {
        if (state === 'in_progress') {
          if (detail && typeof detail === 'string') {
            console.log('[🔵 Upload Progress] Parsing detail:', detail);
            
            // Handle different detail formats
            if (detail.includes('%')) {
              // Extract percentage from detail like "45%" or "Uploading... 45%"
              const percentMatch = detail.match(/(\d+)%/);
              if (percentMatch) {
                const percent = parseInt(percentMatch[1]);
                console.log('[🔵 Upload Progress] Extracted percent:', percent);
                
                if (percent < 25) {
                  setCurrentStep('compressing');
                  setProgress(20 + percent);
                } else {
                  setCurrentStep('uploading');
                  setProgress(30 + Math.round(percent * 0.5));
                }
              }
            } else if (detail.toLowerCase().includes('preparing')) {
              console.log('[🔵 Upload Progress] Setting to validating');
              setCurrentStep('validating');
              setProgress(5);
            } else {
              setCurrentStep('uploading');
              setProgress(40);
            }
          } else {
            setCurrentStep('uploading');
            setProgress(40);
          }
        } else if (state === 'success') {
          console.log('[🔵 Upload Progress] Upload complete');
          setProgress(80);
        } else if (state === 'error') {
          console.log('[🔵 Upload Progress] Upload error');
          setCurrentStep('error');
        }
      } else if (key === 'save') {
        if (state === 'in_progress') {
          console.log('[🔵 Upload Progress] Saving...');
          setCurrentStep('saving');
          setProgress(90);
        } else if (state === 'success') {
          console.log('[🔵 Upload Progress] Save complete!');
          setCurrentStep('done');
          setProgress(100);
        } else if (state === 'error') {
          console.log('[🔵 Upload Progress] Save error');
          setCurrentStep('error');
        }
      }
    };

    window.addEventListener('post-flow:update' as any, handleFlowUpdate);
    
    // Timeout protection
    const timeoutId = setTimeout(() => {
      if (isUploading && currentStep !== 'done' && currentStep !== 'error') {
        console.error('[Upload Progress] Upload timed out after 5 minutes');
        setTimeoutError(true);
        setCurrentStep('error');
        onDismiss?.();
      }
    }, UPLOAD_TIMEOUT);

    return () => {
      window.removeEventListener('post-flow:update' as any, handleFlowUpdate);
      clearTimeout(timeoutId);
    };
  }, [isUploading, currentStep, onDismiss]);

  // Auto-dismiss success after 3 seconds
  useEffect(() => {
    if (currentStep === 'done') {
      const timer = setTimeout(() => {
        onDismiss?.();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [currentStep, onDismiss]);

  if (!isUploading && currentStep !== 'done') return null;

  const stepInfo = STEP_INFO[currentStep];
  const isError = currentStep === 'error' || timeoutError;
  const isDone = currentStep === 'done';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ x: 400, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 400, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed bottom-6 right-6 z-50 w-full max-w-sm"
      >
        <div className={cn(
          "rounded-xl border shadow-2xl overflow-hidden backdrop-blur-md",
          isDone && "bg-green-500/10 border-green-500/30",
          isError && "bg-red-500/10 border-red-500/30",
          !isDone && !isError && "bg-black/80 border-purple-500/30"
        )}>
          {/* Progress bar */}
          <div className="relative h-1 bg-white/5">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
              className={cn(
                "absolute h-full",
                isDone && "bg-green-500",
                isError && "bg-red-500",
                !isDone && !isError && "bg-purple-500"
              )}
            />
          </div>

          {/* Content */}
          <div className="p-4 flex items-center gap-3">
            {/* Icon */}
            <div className={cn(
              "flex-shrink-0",
              isDone && "text-green-500",
              isError && "text-red-500",
              !isDone && !isError && "text-purple-500"
            )}>
              {isDone ? (
                <CheckCircle className="h-6 w-6" />
              ) : isError ? (
                <XCircle className="h-6 w-6" />
              ) : (
                <Loader2 className="h-6 w-6 animate-spin" />
              )}
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm">
                {timeoutError ? 'Upload timed out' : stepInfo.label}
              </p>
              {!isDone && !isError && (
                <p className="text-gray-400 text-xs mt-0.5">
                  {progress}% complete
                </p>
              )}
              {timeoutError && (
                <p className="text-red-400 text-xs mt-0.5">
                  Upload timed out after 5 minutes. Please check your connection and try again.
                </p>
              )}
            </div>

            {/* Dismiss button */}
            {(isDone || isError) && (
              <button
                onClick={onDismiss}
                className="flex-shrink-0 text-gray-400 hover:text-white transition-colors p-1"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
